import React, { useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { ArrowRight, ShieldCheck, Users, TrendingUp, Zap, CheckCircle2, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { RECRUITER_WHATSAPP_LINK } from '../constants/externalLinks';

// ──────────────────────────────────────────────────────────
// Animated SVG mesh background (subtle, grid-like)
// ──────────────────────────────────────────────────────────
const MeshBackground: React.FC = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="mesh-fade" cx="50%" cy="0%" r="70%">
        <stop offset="0%" stopColor="#0f172a" stopOpacity="0.04" />
        <stop offset="100%" stopColor="#0f172a" stopOpacity="0" />
      </radialGradient>
      <pattern id="small-grid" width="40" height="40" patternUnits="userSpaceOnUse">
        <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(15,23,42,0.06)" strokeWidth="0.5" />
      </pattern>
      <pattern id="big-grid" width="200" height="200" patternUnits="userSpaceOnUse">
        <rect width="200" height="200" fill="url(#small-grid)" />
        <path d="M 200 0 L 0 0 0 200" fill="none" stroke="rgba(15,23,42,0.08)" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#big-grid)" />
    <rect width="100%" height="100%" fill="url(#mesh-fade)" />
  </svg>
);

// ──────────────────────────────────────────────────────────
// 3D tilt phone mockup
// ──────────────────────────────────────────────────────────
const PhoneMockup: React.FC = () => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(useSpring(y, { stiffness: 100, damping: 20 }), [-0.5, 0.5], [6, -6]);
  const rotateY = useTransform(useSpring(x, { stiffness: 100, damping: 20 }), [-0.5, 0.5], [-6, 6]);

  const handleMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  const handleLeave = () => { x.set(0); y.set(0); };

  // Floating cards data
  const pills = [
    {
      icon: <ShieldCheck size={15} className="text-emerald-600" />,
      label: 'MEA Verified',
      sub: '100% Compliant',
      bg: 'bg-emerald-50 border-emerald-100',
      pos: 'top-20 -left-[168px]',
      anim: [0, -14, 0],
    },
    {
      icon: <Users size={15} className="text-blue-600" />,
      label: '1,204 Applied',
      sub: 'Verified Pipeline',
      bg: 'bg-blue-50 border-blue-100',
      pos: 'bottom-36 -left-[180px]',
      anim: [0, 14, 0],
    },
    {
      icon: <TrendingUp size={15} className="text-violet-600" />,
      label: 'Hire Rate +38%',
      sub: 'vs. last quarter',
      bg: 'bg-violet-50 border-violet-100',
      pos: 'top-32 -right-[168px]',
      anim: [0, -10, 0],
    },
    {
      icon: <Zap size={15} className="text-amber-500" />,
      label: '12 Interviews',
      sub: 'Scheduled today',
      bg: 'bg-amber-50 border-amber-100',
      pos: 'bottom-52 -right-[176px]',
      anim: [0, 12, 0],
    },
  ];

  return (
    <div
      className="relative hidden lg:flex items-center justify-center h-[680px] w-full"
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
    >
      {/* Ambient glow */}
      <div className="absolute w-80 h-80 bg-blue-100 rounded-full blur-[100px] opacity-70 pointer-events-none" />

      {/* Phone */}
      <motion.div
        initial={{ opacity: 0, y: 60, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.0, delay: 0.3, type: 'spring', stiffness: 70 }}
        style={{ rotateX, rotateY, perspective: 1000, transformStyle: 'preserve-3d' }}
        className="relative z-20"
      >
        {/* Outer shell — ultra-thin silver rim */}
        <div className="relative w-[280px] h-[572px] rounded-[3.2rem] bg-gradient-to-b from-slate-300 to-slate-400 p-[1.5px] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.18),0_0_0_0.5px_rgba(255,255,255,0.4)_inset]">
          <div className="relative w-full h-full rounded-[3rem] bg-white overflow-hidden">
            {/* Dynamic Island */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[104px] h-[30px] rounded-full z-20 overflow-hidden bg-slate-950 flex items-center justify-center gap-3">
              <div className="w-[10px] h-[10px] rounded-full bg-slate-800 ring-2 ring-slate-700" />
              <div className="w-[5px] h-[5px] rounded-full bg-slate-700" />
            </div>
            {/* Screen */}
            <img
              src="/recss.jpeg"
              alt="Jobisa Recruiter App"
              className="w-full h-full object-cover object-top"
            />
            {/* Status bar gradient */}
            <div className="absolute top-0 inset-x-0 h-14 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
            {/* Reflection sheen */}
            <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-tl from-transparent via-white/[0.04] to-white/[0.08] pointer-events-none" />
          </div>
        </div>

        {/* Side buttons */}
        <div className="absolute top-28 -left-[3px] w-[3px] h-10 bg-slate-300 rounded-l-full" />
        <div className="absolute top-44 -left-[3px] w-[3px] h-8 bg-slate-300 rounded-l-full" />
        <div className="absolute top-36 -right-[3px] w-[3px] h-14 bg-slate-300 rounded-r-full" />
      </motion.div>

      {/* Floating insight pills */}
      {pills.map((pill, i) => (
        <motion.div
          key={i}
          animate={{ y: pill.anim }}
          transition={{ repeat: Infinity, duration: 5 + i, ease: 'easeInOut', delay: i * 0.4 }}
          initial={{ opacity: 0, scale: 0.85 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className={`absolute z-30 ${pill.pos} min-w-[152px] rounded-2xl border bg-white shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-3.5 flex items-center gap-3`}
        >
          <div className={`w-9 h-9 rounded-xl border flex items-center justify-center ${pill.bg} shrink-0`}>
            {pill.icon}
          </div>
          <div>
            <p className="text-[13px] font-bold text-slate-900 leading-tight">{pill.label}</p>
            <p className="text-[11px] text-slate-500 font-medium mt-0.5">{pill.sub}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// Social proof logos strip (fake, for composition)
// ──────────────────────────────────────────────────────────
const agencies = ['Al Rashed', 'Gulf Stars', 'Sky Manpower', 'Emirates HR', 'GCC Talent', 'PeopleBridge'];

// ──────────────────────────────────────────────────────────
// MAIN HERO
// ──────────────────────────────────────────────────────────
const Hero: React.FC = () => {
  return (
    <section className="relative bg-white min-h-screen flex flex-col justify-center overflow-hidden pt-20">
      <MeshBackground />

      {/* Top accent line */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-12 pt-16 pb-8">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 xl:gap-20 items-center">

          {/* ── LEFT ── */}
          <div>
            {/* Trust pill */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-8"
            >
              <CheckCircle2 size={13} className="text-blue-600" />
              <span className="text-[12.5px] font-semibold text-blue-700 tracking-wide">The enterprise standard for international recruitment</span>
            </motion.div>

            {/* Giant editorial headline */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-black text-slate-950 leading-[1.04] tracking-tight mb-6"
              style={{
                fontSize: 'clamp(42px, 6vw, 76px)',
                fontFamily: "'Inter', system-ui, sans-serif",
              }}
            >
              Hire India's<br />
              <span className="relative inline-block">
                best workers.
                {/* Underline accent */}
                <motion.span
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.6, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute -bottom-1 left-0 right-0 h-[4px] rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 origin-left"
                />
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-500 to-violet-600">
                Digitally.
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="text-[17px] leading-[1.7] text-slate-500 max-w-lg mb-10 font-normal"
            >
              Jobisa is the all-in-one recruitment OS for agencies placing blue-collar talent across the Gulf & Europe. Campaign management, MEA compliance, interviews — one platform.
            </motion.p>

            {/* CTA row */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-14"
            >
              <Link
                to="/login"
                className="group inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-semibold text-[15px] px-7 py-3.5 rounded-full transition-all shadow-[0_4px_16px_rgba(15,23,42,0.3)] hover:shadow-[0_8px_24px_rgba(15,23,42,0.22)] hover:-translate-y-px"
              >
                Start for Free
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href={RECRUITER_WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-emerald-300 bg-emerald-50 px-6 py-3.5 text-[15px] font-semibold text-emerald-700 transition-all hover:border-emerald-400 hover:bg-emerald-100 hover:-translate-y-px"
              >
                <MessageCircle size={16} />
                Chat on WhatsApp
              </a>
              <a
                href="#features"
                className="text-[15px] font-medium text-slate-500 hover:text-slate-900 underline-offset-4 hover:underline transition-all flex items-center gap-1"
              >
                See what's inside →
              </a>
            </motion.div>

            {/* Stat strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.55 }}
            >
              <div className="flex flex-wrap gap-x-10 gap-y-4 border-t border-slate-100 pt-8">
                {[
                  { val: '15k+', label: 'Verified Candidates' },
                  { val: '40%', label: 'Faster Time-to-Hire' },
                  { val: '100%', label: 'MEA Compliant' },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-2xl font-black text-slate-950 tracking-tight leading-none mb-1">{s.val}</p>
                    <p className="text-[12px] text-slate-400 font-medium uppercase tracking-widest">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* ── RIGHT: Phone ── */}
          <PhoneMockup />
        </div>
      </div>

      {/* Agency trust bar */}
      <div className="relative z-10 border-t border-slate-100 py-6 overflow-hidden mt-4">
        <p className="text-center text-[11.5px] font-semibold uppercase tracking-[0.2em] text-slate-400 mb-5">
          Trusted by leading agencies
        </p>
        <div className="flex justify-center flex-wrap gap-x-10 gap-y-2 px-6">
          {agencies.map(name => (
            <span key={name} className="text-[13px] font-bold text-slate-300 tracking-wide whitespace-nowrap">
              {name}
            </span>
          ))}
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 inset-x-0 h-28 bg-gradient-to-t from-slate-50 to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
